import { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send, Instagram } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: 'General enquiry', message: '' });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast({ title: 'Message received', description: 'We\'ll write back within 24 hours — promise.' });
      setForm({ name: '', email: '', phone: '', subject: 'General enquiry', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: err?.response?.data?.detail || 'Try again shortly.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24 sm:pt-32 pb-16 paper-texture">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <div className="flex justify-center mb-3 sm:mb-4"><span className="leaf-divider">Say hello</span></div>
          <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl font-bold text-[#1F4D3A] leading-tight mb-4 sm:mb-6">
            Let's plan your <em className="text-[#C87A50] not-italic">next road story</em>.
          </h1>
          <p className="text-base sm:text-lg text-[#4A3728] max-w-2xl mx-auto">
            Have a region in mind or an idea for a private trip? Drop us a note, or find us at any of the numbers below.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact cards */}
          <div className="space-y-4">
            {[
              { icon: Phone, title: 'Call us', v: '+91 96060 94060', v2: 'Mon–Sat, 9am–8pm IST', href: 'tel:+919606094060', testid: 'contact-card-phone' },
              { icon: Mail, title: 'Write to us', v: 'contact@onwheels.cc', v2: 'Reply within 24 hours', href: 'mailto:contact@onwheels.cc', testid: 'contact-card-email' },
              { icon: MessageCircle, title: 'WhatsApp', v: '+91 96060 94060', v2: 'Quickest for quotes', href: `https://wa.me/919606094060?text=${encodeURIComponent("Hi, I'd like to know more about your tours.")}`, external: true, testid: 'contact-card-whatsapp' },
              { icon: Instagram, title: 'Instagram', v: '@on_wheels.co', v2: 'Follow our stories', href: 'https://www.instagram.com/on_wheels.co', external: true, testid: 'contact-card-instagram' },
              { icon: MapPin, title: 'Come by', v: 'Bharath Nagar, Bengaluru', v2: 'Meetings by appointment', href: 'https://maps.google.com/maps?q=Bharath+Nagar,+Bengaluru', external: true, testid: 'contact-card-address' }
            ].map((c) => (
              <a
                key={c.title}
                href={c.href}
                target={c.external ? '_blank' : undefined}
                rel={c.external ? 'noopener noreferrer' : undefined}
                data-testid={c.testid}
                className="block bg-white rounded-2xl p-6 border border-[#E5DDCB] hover:border-[#1F4D3A] hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#F5F0E8] flex items-center justify-center flex-shrink-0">
                    <c.icon className="w-5 h-5 text-[#1F4D3A]" />
                  </div>
                  <div>
                    <h4 className="font-serif text-lg font-semibold text-[#1F4D3A]">{c.title}</h4>
                    <p className="text-sm text-[#4A3728] font-medium mt-1 break-all">{c.v}</p>
                    <p className="text-xs text-[#6B8E5A] mt-0.5">{c.v2}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Form */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E5DDCB] p-8 md:p-10">
            <h3 className="font-serif text-3xl font-bold text-[#1F4D3A] mb-2">Send us a note</h3>
            <p className="text-sm text-[#6B8E5A] mb-6">The more you share, the better we can tailor.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#6B8E5A] mb-2 font-semibold">Your name</label>
                  <input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#6B8E5A] mb-2 font-semibold">Email</label>
                  <input required type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#6B8E5A] mb-2 font-semibold">Phone</label>
                  <input required value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#6B8E5A] mb-2 font-semibold">Subject</label>
                  <select value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})} className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A]">
                    <option>General enquiry</option>
                    <option>Custom / private trip</option>
                    <option>Corporate travel</option>
                    <option>Group booking</option>
                    <option>Careers</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#6B8E5A] mb-2 font-semibold">Your message</label>
                <textarea required rows={6} value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} placeholder="Tell us about your dream trip — dates, region, group size…" className="w-full px-4 py-3 rounded-lg bg-[#F7F3EA] border border-[#E5DDCB] text-sm focus:outline-none focus:border-[#1F4D3A] resize-none" />
              </div>
              <Button type="submit" disabled={loading} className="btn-primary bg-[#1F4D3A] hover:bg-[#2D6B4F] text-white rounded-full px-10 h-12">
                {loading ? 'Sending…' : (<><Send className="w-4 h-4 mr-2" /> Send message</>)}
              </Button>
            </form>
          </div>
        </div>

        {/* Map — Bharath Nagar, Bengaluru */}
        <div className="mt-16 rounded-3xl overflow-hidden border border-[#E5DDCB] h-96" data-testid="contact-map">
          <iframe
            title="On Wheels — Bharath Nagar, Bengaluru"
            src="https://maps.google.com/maps?q=Bharath+Nagar,+Bengaluru&t=&z=15&ie=UTF8&iwloc=&output=embed"
            className="w-full h-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </main>
  );
}
